import moment from 'moment'
import ISystemStatusMessage from '../model/ISystemStatusMessage'
import { DATE_TIME_FORMAT, formatUtcDateTime } from './dateFormat'

// The app-wide date/time format, shared by the table, the banner and the
// form's date pickers. All status message times are UTC.
export { DATE_TIME_FORMAT, formatUtcDateTime }

// The pickers render DATE_TIME_FORMAT, but nobody types a month as letters --
// they type or paste whatever their habits produce, so accept the common shapes
// and render the result back in the one format the module displays.
const DATE_SHAPES = [
  'DD MMM YYYY',
  'D MMM YYYY',
  'DD MMMM YYYY',
  'D MMMM YYYY',
  'DD/MM/YYYY',
  'D/M/YYYY',
  'DD-MM-YYYY',
  'D-M-YYYY',
  'DD.MM.YYYY',
  'D.M.YYYY',
  'YYYY-MM-DD'
]

const TIME_SHAPES = ['h:mm A', 'h:mmA', 'HH:mm', 'H:mm']

const ACCEPTED_INPUT_FORMATS = [
  ...DATE_SHAPES.flatMap(date => TIME_SHAPES.map(time => `${date} ${time}`)),
  'YYYY-MM-DDTHH:mm',
  'DD/MM/YYYY HH:mm:ss',
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DDTHH:mm:ss.SSSZ',
  ...DATE_SHAPES
]

// `null` for anything unparseable, including the empty string -- callers that
// need to tell "cleared" from "typo" check the raw text first.
export const parseUserDateTime = (raw: string): moment.Moment | null => {
  const trimmed = raw.trim()
  if (!trimmed) {
    return null
  }
  const parsed = moment.utc(trimmed, ACCEPTED_INPUT_FORMATS, true)
  return parsed.isValid() ? parsed : null
}

const UTC_SUFFIX = '(UTC)'

// The banner "schedule / time context" line, derived from the active window.
// The backend has no free-form schedule text, so we render the start/expiry
// window in UTC.
export const formatBannerSchedule = (
  msg: Pick<ISystemStatusMessage, 'startDate' | 'expiringDate'>
): string =>
  `Scheduled from ${formatUtcDateTime(
    msg.startDate
  )} ${UTC_SUFFIX} until ${formatUtcDateTime(msg.expiringDate)} ${UTC_SUFFIX}`

export const isoToPickerMoment = (iso: string): moment.Moment | null => {
  if (!iso) {
    return null
  }
  const m = moment.utc(iso)
  return m.isValid() ? m : null
}

export const pickerMomentToIso = (m: moment.Moment | null): string =>
  m && m.isValid() ? m.toISOString() : ''

export const nowIsoToTheMinute = (): string =>
  moment
    .utc()
    .startOf('minute')
    .toISOString()
